import i18n from 'i18next';

interface Template {
  key: string;
  pattern: RegExp;
  /** Regex group name -> original placeholder name (dots are not legal in group names). */
  names: Record<string, string>;
  /** Characters outside placeholders; more literal text means a more specific template. */
  literalLength: number;
}

// Templates with less literal text than this ("{{value}}%", "{{a}} {{b}}")
// would match almost any message, so they are never used for lookup.
const MIN_LITERAL_LENGTH = 3;

// Translated sentences are short (the longest is under 200 characters). A
// much longer message is a raw server error or a stack trace, not a
// translation, so pattern matching is skipped for it. This also keeps regex
// backtracking bounded.
const MAX_TEMPLATE_MESSAGE_LENGTH = 1000;

interface ReverseIndex {
  /**
   * Fixed sentences: translated text -> its key. When two keys share the same
   * text, the one that appears first in the translation file is kept.
   */
  exact: Map<string, string>;
  /** Sentences with a blank ({{placeholder}}), each turned into a pattern. */
  templates: Template[];
}

const NAMESPACE = 'translation';
const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;
const HAS_PLACEHOLDER = /\{\{\s*[\w.]+\s*\}\}/;

const indexByLanguage = new Map<string, ReverseIndex>();

/**
 * The translation file is a tree (settings > settingsTab > passUpdateSuccess).
 * To find a key from its text we need the reverse of `t()`, so this walks the
 * tree once and returns flat pairs of `[dotted.key, text]`.
 */
function flatten (translations: unknown, prefix = '', out: Array<[string, string]> = []): Array<[string, string]> {
  if (typeof translations !== 'object' || translations === null) {
    return out;
  }
  for (const [k, v] of Object.entries(translations as Record<string, unknown>)) {
    const key = prefix === '' ? k : `${prefix}.${k}`;
    if (typeof v === 'string') {
      out.push([key, v]);
    } else {
      flatten(v, key, out);
    }
  }
  return out;
}

/** Makes a literal piece of text safe to drop into a regex (dots, parens, etc.). */
function escapeRegExp (s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Translations with a blank, like "Marcando a {{number}}", reach the logger
 * already filled in ("Marcando a 5551234"), so they can't be found by exact
 * text. This turns such a template into a pattern that asks "does the message
 * have this shape, and what was in the blank?":
 * "Marcando a {{number}}" -> /^Marcando a (?<number>.+?)$/
 * Returns null for templates too generic to be trusted ("{{value}}%").
 */
function compileTemplate (key: string, text: string): Template | null {
  let source = '';
  let last = 0;
  let literalLength = 0;
  const names: Record<string, string> = {};

  for (const m of text.matchAll(PLACEHOLDER)) {
    const group = m[1].replace(/\./g, '_');
    if (group in names) {
      return null; // repeated placeholder; a regex can't name two groups alike
    }

    names[group] = m[1];
    const literal = text.slice(last, m.index);
    literalLength += literal.trim().length;
    source += escapeRegExp(literal);
    source += `(?<${group}>.+?)`;
    last = (m.index ?? 0) + m[0].length;
  }

  const tail = text.slice(last);
  literalLength += tail.trim().length;
  source += escapeRegExp(tail);
  if (literalLength < MIN_LITERAL_LENGTH) {
    return null;
  }

  return { key, pattern: new RegExp(`^${source}$`, 's'), names, literalLength };
}

/**
 * Builds the lookup for one language: a text -> key table for the fixed
 * sentences, and the list of patterns for sentences with a blank, ordered so
 * the most specific pattern is tried first.
 */
function buildIndex (lng: string): ReverseIndex {
  const exact = new Map<string, string>();
  const templates: Template[] = [];
  const translations: unknown = i18n.getResourceBundle(lng, NAMESPACE);

  for (const [key, text] of flatten(translations)) {
    if (text.trim() === '') {
      continue; // an empty translation must never claim an empty log message
    }
    if (HAS_PLACEHOLDER.test(text)) {
      const template = compileTemplate(key, text);
      if (template !== null) {
        templates.push(template);
      }
    } else if (!exact.has(text)) {
      exact.set(text, key);
    }
  }

  // Most specific first, so "{{n}} citas de {{m}} transferencias · {{p}}%"
  // is tried before a generic "{{value}}%" that would swallow it.
  templates.sort((a, b) => b.literalLength - a.literalLength);

  return { exact, templates };
}

/** Returns the lookup for a language, building it on first use and caching it. */
function getIndex (lng: string): ReverseIndex {
  let index = indexByLanguage.get(lng);
  if (index === undefined) {
    index = buildIndex(lng);
    indexByLanguage.set(lng, index);
  }
  return index;
}

/** The language the app is showing right now ("es"), or undefined before i18n is ready. */
function currentLanguage (): string | undefined {
  const lng = i18n.resolvedLanguage ?? i18n.language;
  return typeof lng === 'string' && lng.trim() !== '' ? lng : undefined;
}

/**
 * Renders a key with the English translation file, filling any blanks with
 * `values`. Returns undefined when the key has no English text, so the caller
 * can keep the original message instead of logging the key itself.
 * Values go through `replace` so a blank named like an i18next option
 * ("count", "context", "lng") is treated as plain text, not as a setting.
 */
function translateToEnglish (key: string, values: Record<string, string> = {}): string | undefined {
  if (!i18n.exists(key, { lng: 'en' })) {
    return undefined;
  }
  return i18n.t(key, { lng: 'en', replace: values });
}

function lookup (message: string, index: ReverseIndex): string | undefined {
  const exactKey = index.exact.get(message);
  if (exactKey !== undefined) {
    return translateToEnglish(exactKey);
  }

  if (message.length > MAX_TEMPLATE_MESSAGE_LENGTH) {
    return undefined;
  }

  for (const { key, pattern, names } of index.templates) {
    const m = pattern.exec(message);
    if (m !== null) {
      const values: Record<string, string> = {};
      for (const [group, value] of Object.entries(m.groups ?? {})) {
        values[names[group]] = value;
      }
      return translateToEnglish(key, values);
    }
  }

  return undefined;
}

/**
 * Maps a message that was already translated into the app language back to
 * its English text, so logs are always English while toasts keep the app
 * language. English is the app default and every translated string has an
 * English source, so a translated message always resolves; anything that is
 * not in the active translation file (English literals, server errors) is returned as is.
 *
 * Never throws: logging must not fail because of the translation step, so any
 * error here falls back to the original message.
 */
export function toEnglishLogMessage (message: string): string {
  try {
    const lng = currentLanguage();
    if (lng === undefined || lng.startsWith('en')) {
      return message;
    }
    return lookup(message, getIndex(lng)) ?? message;
  } catch (err) {
    console.warn('[Logger] Could not map a log message to English:', err);
    return message;
  }
}
